import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef } from "react";
import { captureApolloError } from "services/external/sentry";
import type { Fridge } from "types";
import { networkTrace, shouldTraceNetworkDebug } from "utils/debug/networkTrace";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";

// While the stream stays broken (and online) we keep re-attempting the AppSync handshake on an
// exponential backoff, capped so a persistent zombie retries at most once per minute.
const RECONNECT_RETRY_BASE_MS = 5000;
const RECONNECT_RETRY_MAX_MS = 60_000;

type ReconnectArgs = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
	/**
	 * Re-subscribes the live FRIDGE_SUBSCRIPTION (Apollo useSubscription's restart()).
	 * The AppSync link errors the observable on a dropped socket and never re-opens it, and
	 * RetryLink only retries a few times before giving up — so on recovery the refetch below
	 * restores initial state but the live stream stays dead unless we restart it here too.
	 */
	restartSubscription: () => void;
};

/**
 * Owns the "re-fetch initial fridge state after the live subscription broke" retry.
 *
 * Defects fixed vs. the old inline DeviceContext logic:
 * - The retry setTimeout is tracked in a ref and cleared on cleanup, so it can't fire after unmount.
 * - The reconnect function is stabilized with useCallback and its effect deps only change on real
 *   state transitions, so the recovery effect runs one backoff loop per outage (restarting the live
 *   stream immediately, then on an exponential backoff) instead of re-firing on every provider re-render.
 * - onData is kept current via a ref, so a fresh onData identity each render does NOT re-trigger the
 *   reconnect effect.
 */
export const useReconnectAfterBrokenSubscription = ({
	isSubscriptionBroken,
	appConnected,
	fridgeId,
	refetch,
	onData,
	restartSubscription,
}: ReconnectArgs): void => {
	const onDataRef = useRef(onData);
	const restartSubscriptionRef = useRef(restartSubscription);
	const retryTimeoutRef = useRef<number | null>(null);
	const backoffAttemptRef = useRef(0);
	// Tracks the previous appConnected so the effect below can detect the false→true rising edge.
	const prevAppConnectedRef = useRef(appConnected);
	// Single-flight guard for the initial-state refetch. The broken-subscription lane and the
	// connectivity-rising lane fire TOGETHER on the common outage shape (retryConnectionPolicy flips
	// broken=true and connected=false on the first error, so recovery flips connected back while broken
	// is still true) — without this, that double-fired the refetch and its "Sending initial query"
	// warning every episode. True while a flight (including its 1s empty-response retries) is running;
	// released at every terminal: success, retry limit, or throw.
	const refetchInFlightRef = useRef(false);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	// Kept in a ref so a fresh restartSubscription identity each render (it depends on
	// shouldSkipSubscription in the provider) does NOT re-trigger the recovery effect.
	useEffect(() => {
		restartSubscriptionRef.current = restartSubscription;
	}, [restartSubscription]);

	const handleReconnectAfterBrokenSubscription = useCallback(
		async (retryCount: number): Promise<void> => {
			if (retryCount > 2) {
				captureApolloError(new Error("Failed to load initial fridge state 3 times"), {
					kind: "Initial_Query_Error_Retries_Limit",
					query: INITIAL_STATE_QUERY.kind,
					variables: { fridgeId: fridgeId },
				});
				refetchInFlightRef.current = false;
				return;
			}
			try {
				if (shouldTraceNetworkDebug()) {
					networkTrace(`[ws] reconnect: refetch attempt #${retryCount + 1}`);
				}
				logToSentry(`Sending initial query after broken subscription on fridge: ${fridgeId}`, "warning");
				const res = await refetch();
				if (res.data?.fridge) {
					if (shouldTraceNetworkDebug()) {
						networkTrace("[ws] reconnect: refetch ok → initial state restored");
					}
					logToSentry(`Setting initial data after broken subscription fridge: ${fridgeId}`, "warning", [
						res.data.fridge,
					]);
					onDataRef.current(res.data.fridge);
					refetchInFlightRef.current = false;
				} else {
					if (shouldTraceNetworkDebug()) {
						networkTrace(`[ws] reconnect: refetch empty (retry #${retryCount + 1})`);
					}
					captureApolloError(
						new Error("Initial query error"),
						{
							kind: "Initial_Query_Error",
							query: INITIAL_STATE_QUERY.kind,
							variables: { fridgeId: fridgeId },
						},
						{
							ResponseData: res,
						},
					);
					retryTimeoutRef.current = window.setTimeout(() => {
						handleReconnectAfterBrokenSubscription(retryCount + 1);
					}, 1000);
				}
			} catch (err) {
				Sentry.captureException(err);
				refetchInFlightRef.current = false;
			}
		},
		[fridgeId, refetch],
	);

	// The ONLY entry point for a new refetch flight — both recovery lanes below call this. When the
	// other lane already started this episode's flight, piggyback on it instead of double-firing the
	// query (and its "Sending initial query" warning). The 1s empty-response retries bypass this by
	// calling handleReconnectAfterBrokenSubscription directly with retryCount > 0 — they are the SAME
	// flight, not a new one.
	const startInitialStateRefetch = useCallback(() => {
		if (refetchInFlightRef.current) {
			return;
		}
		refetchInFlightRef.current = true;
		handleReconnectAfterBrokenSubscription(0);
	}, [handleReconnectAfterBrokenSubscription]);

	useEffect(() => {
		if (!(isSubscriptionBroken && appConnected && fridgeId)) {
			// Healthy again (or offline): reset so the next outage starts from the base delay.
			backoffAttemptRef.current = 0;
			return;
		}
		let timer: number | null = null;
		// Resume the live stream (a new AppSync handshake); the first frame that arrives clears
		// isSubscriptionBroken via onData. A single restart() can yield another dead socket, and the
		// AppSync link never re-opens a dropped socket on its own, so while the stream stays broken we
		// keep re-attempting the handshake on an exponential backoff — a fresh handshake is far cheaper
		// than the page-reload backstop, so most zombies recover via a live frame before a reload.
		const attemptRestart = (): void => {
			if (shouldTraceNetworkDebug()) {
				networkTrace("[ws] reconnect: restart subscription");
			}
			restartSubscriptionRef.current();
			const delay = Math.min(RECONNECT_RETRY_BASE_MS * 2 ** backoffAttemptRef.current, RECONNECT_RETRY_MAX_MS);
			backoffAttemptRef.current += 1;
			timer = window.setTimeout(attemptRestart, delay);
		};
		// Immediate restart (t≈0), then the one-shot initial-state refetch. The refetch stays outside the
		// backoff loop — it restores initial state once per outage and must not re-fire on every retry.
		attemptRestart();
		startInitialStateRefetch();
		return () => {
			if (timer !== null) {
				window.clearTimeout(timer);
			}
		};
	}, [isSubscriptionBroken, appConnected, fridgeId, startInitialStateRefetch]);

	// Connectivity recovered (appConnected false→true) without a FridgeSubscription error having flipped
	// isSubscriptionBroken — e.g. a non-subscription op tripped offline, or the socket recovered without
	// surfacing an observable error. We may have missed sparse deltas during the gap (notably the BE's
	// post-transaction QR rotation, a one-shot frame), so re-pull authoritative state via the same
	// network-only refetch the broken-subscription path uses. Refetch ONLY: the live stream wasn't
	// necessarily broken, so we must not force an AppSync re-handshake here (restart stays owned by the
	// isSubscriptionBroken branch above). This lane still fires while isSubscriptionBroken is true —
	// that covers the case where isSubscriptionBroken stays stuck true on a long-idle fridge (it only
	// clears on the next subscription frame) — but through the single-flight entry, so when the broken
	// lane above already started this episode's refetch (the common shape: both flags flip back in one
	// commit), this piggybacks on that flight instead of double-firing the query.
	useEffect(() => {
		const regainedConnectivity = !prevAppConnectedRef.current && appConnected;
		prevAppConnectedRef.current = appConnected;
		if (regainedConnectivity && fridgeId) {
			if (shouldTraceNetworkDebug()) {
				networkTrace("[ws] reconnect: connectivity regained → refetch");
			}
			startInitialStateRefetch();
		}
	}, [appConnected, fridgeId, startInitialStateRefetch]);

	useEffect(
		() => () => {
			if (retryTimeoutRef.current !== null) {
				window.clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
		},
		[],
	);
};
